import { Shield, ShieldCheck, ShieldOff } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import type { ApiError } from '@/core/api';
import {
  useConfirmMfa,
  useDisableMfa,
  useMfaStatus,
  useStartMfaSetup,
} from '../queries';
import type { MfaSetupResponse } from '../types';

/** Pull the first field-error message out of a 400 response, if any. */
function fieldError(err: unknown, name: string): string | null {
  const fe = (err as ApiError | undefined)?.fieldErrors?.[name];
  return fe && fe.length > 0 ? fe.join(' ') : null;
}

export function SecurityPage() {
  const status = useMfaStatus();
  const startSetup = useStartMfaSetup();
  const confirm = useConfirmMfa();
  const disable = useDisableMfa();

  /** Enrollment payload, present once `startSetup` succeeds and not yet confirmed. */
  const [setupData, setSetupData] = useState<MfaSetupResponse | null>(null);
  const [confirmCode, setConfirmCode] = useState('');
  const [disableCode, setDisableCode] = useState('');

  const enabled = status.data?.enabled ?? false;

  async function onStartSetup() {
    const data = await startSetup.mutateAsync();
    setSetupData(data);
    setConfirmCode('');
  }

  async function onConfirm(e: React.FormEvent) {
    e.preventDefault();
    try {
      await confirm.mutateAsync(confirmCode);
      setSetupData(null);
    } catch {
      // Field-level error rendered inline below; nothing else to do.
    }
  }

  async function onDisable(e: React.FormEvent) {
    e.preventDefault();
    try {
      await disable.mutateAsync(disableCode);
      setDisableCode('');
    } catch {
      // Field error surfaced inline below.
    }
  }

  return (
    <div className="max-w-2xl space-y-6">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight">Account security</h1>
        <p className="text-muted-foreground text-sm">
          Protect your account with two-factor authentication (TOTP).
        </p>
      </div>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            {enabled ? (
              <>
                <ShieldCheck className="text-emerald-600 size-5" /> Two-factor authentication is on
              </>
            ) : (
              <>
                <Shield className="size-5" /> Two-factor authentication
              </>
            )}
          </CardTitle>
          <CardDescription>
            {enabled
              ? 'You will be asked for a 6-digit code every time you sign in.'
              : 'Use an authenticator app (Google Authenticator, 1Password, Authy) to add a second step at sign-in.'}
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {status.isPending ? (
            <Skeleton className="h-9 w-40" />
          ) : enabled ? (
            <form className="space-y-3" onSubmit={onDisable}>
              <label className="text-sm font-medium" htmlFor="disable-code">
                Enter a current code to disable
              </label>
              <Input
                id="disable-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                value={disableCode}
                onChange={(e) => setDisableCode(e.target.value)}
                maxLength={10}
                aria-invalid={Boolean(fieldError(disable.error, 'code'))}
              />
              {fieldError(disable.error, 'code') ? (
                <p className="text-destructive text-sm">{fieldError(disable.error, 'code')}</p>
              ) : null}
              <Button
                type="submit"
                variant="destructive"
                disabled={disable.isPending || disableCode.length < 6}
              >
                <ShieldOff className="size-4" />
                {disable.isPending ? 'Disabling…' : 'Disable 2FA'}
              </Button>
            </form>
          ) : setupData ? (
            <form className="space-y-3" onSubmit={onConfirm}>
              <div className="rounded-md border p-3 text-sm">
                <p className="font-medium">Scan or paste this secret into your authenticator</p>
                <code className="bg-muted mt-2 block break-all rounded px-2 py-1 text-xs">
                  {setupData.secret}
                </code>
                <a
                  href={setupData.provisioning_uri}
                  className="text-primary mt-2 inline-block text-xs underline"
                  rel="noreferrer"
                >
                  Open in authenticator app
                </a>
              </div>
              <label className="text-sm font-medium" htmlFor="confirm-code">
                Then enter the 6-digit code your app generates
              </label>
              <Input
                id="confirm-code"
                inputMode="numeric"
                autoComplete="one-time-code"
                placeholder="123456"
                value={confirmCode}
                onChange={(e) => setConfirmCode(e.target.value)}
                maxLength={10}
                aria-invalid={Boolean(fieldError(confirm.error, 'code'))}
              />
              {fieldError(confirm.error, 'code') ? (
                <p className="text-destructive text-sm">{fieldError(confirm.error, 'code')}</p>
              ) : null}
              <div className="flex gap-2">
                <Button type="submit" disabled={confirm.isPending || confirmCode.length < 6}>
                  {confirm.isPending ? 'Verifying…' : 'Verify and enable'}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setSetupData(null);
                    setConfirmCode('');
                  }}
                >
                  Cancel
                </Button>
              </div>
            </form>
          ) : (
            <Button onClick={onStartSetup} disabled={startSetup.isPending}>
              <Shield className="size-4" />
              {startSetup.isPending ? 'Preparing…' : 'Enable 2FA'}
            </Button>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
